import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { navigate } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';

import OfferBannerContext from '../context/OfferBannerContext';
import OfferBannerDetailsQuote from '../details/quote/OfferBannerDetailsQuote';

import { __t, applyTranslations } from 'utils/language';
import { isInfluencerProfileRejected } from 'utils/profile';
import { addThousandsSeparator } from 'utils/helper/common';
import { DEFAULT, FUNC_RETURN_NULL } from '../utils/offer-banner';

import './offer-banner-quote.scss';

applyTranslations(require('./offer-banner-quote.i18n.json'));

import type { OfferBannerTextStyle } from '../types';

const MSG_KEY = 'offer_banner_quote';

const OfferBannerQuote: React.FC = () => {
  const context = React.useContext(OfferBannerContext);
  const {
    userType,
    campaignId,
    subcampaignId,
    offer,
    updatedOffer,
    creatorProfile,
    brandProfileName,
    isSubcampaignRevoked,
    isRenegotiation,
    isQuoteProvided,
    isBrand,
    renderPrimaryText = FUNC_RETURN_NULL,
    renderSecondaryText = FUNC_RETURN_NULL,
    renderGenericText = FUNC_RETURN_NULL,
    renderStatus = FUNC_RETURN_NULL,
    renderDisclaimer = FUNC_RETURN_NULL,
    renderCreatorActions = FUNC_RETURN_NULL,
    onBrandEditOfferAfterReceivingQuote = FUNC_RETURN_NULL
  } = context;

  const currentOffer = isRenegotiation ? updatedOffer : offer;
  const statusText = __t(`${MSG_KEY}.messages.status_pending_${userType}`, {
    creatorName: creatorProfile?.name,
    brandProfileName
  });

  let textStyle = DEFAULT;

  if (!isEmpty(updatedOffer)) {
    textStyle = 'highlighted';
  }

  const renderBrandActions = () => {
    const isCreatorRejected = isInfluencerProfileRejected(creatorProfile);

    if (isCreatorRejected) {
      return null;
    }

    return (
      <div className="offer-banner-quote__actions">
        <ActionButton
          color="tertiary"
          customSize={{ width: '150px' }}
          onClick={() => navigate.to(`/campaigns/${campaignId}/messages?subcampaignId=${subcampaignId}`)}
        >
          {__t(`${MSG_KEY}.labels.message_creator`)}
        </ActionButton>
        <ActionButton
          color="secondary"
          customSize={{ width: '150px' }}
          onClick={onBrandEditOfferAfterReceivingQuote}
        >
          {__t(`${MSG_KEY}.labels.edit_offer`)}
        </ActionButton>
      </div>
    );
  };

  const renderDetails = (): React.ReactNode => (
    <OfferBannerDetailsQuote
      offer={currentOffer}
      textStyle={textStyle as OfferBannerTextStyle}
    />
  );

  const renderCreator = (): React.ReactNode => {
    const primaryText = __t(`${MSG_KEY}.messages.quote_requested`, {
      brandProfileName
    });

    const secondaryText = __t(`${MSG_KEY}.messages.read_brief`);
    const thanksForQuote = __t(`${MSG_KEY}.messages.thanks_for_quote`);

    return (
      <React.Fragment>
        { !isQuoteProvided && renderPrimaryText(primaryText) }
        { !isQuoteProvided && renderSecondaryText(secondaryText) }
        { renderDetails() }
        { isQuoteProvided && renderStatus(statusText) }
        { !isQuoteProvided && renderCreatorActions() }
        { isQuoteProvided && renderGenericText(thanksForQuote) }
      </React.Fragment>
    );
  };

  const renderBrand = (): React.ReactNode => {
    const secondaryText = __t(`${MSG_KEY}.labels.expected_compensation`);

    return (
      <React.Fragment>
        { renderDetails() }
        {
          isQuoteProvided && (
            <React.Fragment>
              <div className="offer-banner-quote__secondary-text">
                {renderSecondaryText(secondaryText)}
              </div>
              <div className="offer-banner-quote__provided-amount">
                {addThousandsSeparator(currentOffer?.quote || 0)}€
              </div>
            </React.Fragment>
          )
        }
        { !isQuoteProvided && renderStatus(statusText) }
        { isQuoteProvided && renderBrandActions() }
      </React.Fragment>
    );
  };

  const containerClasses = classnames('offer-banner-quote', {
    [`offer-banner-quote--${currentOffer?.state}`]: !isSubcampaignRevoked
  });

  return (
    <React.Fragment>
      <div className={containerClasses}>
        {
          isBrand
            ? renderBrand()
            : renderCreator()
        }
      </div>
      { renderDisclaimer(isQuoteProvided ? 'quote_submitted' : 'quote_pending') }
    </React.Fragment>
  );
};

export default OfferBannerQuote;
